import React, { useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { Api_Url } from "../../../../../../utils/ApiUrl";
import useFileUpload from "../../../../../../hooks/dataSenders/singleUpload";
import Cropper from "react-easy-crop";
import { useCallback } from "react";
const EditInfoModal = ({
  show,
  handleClose,
  basicInfo,
  setBasicInfo,
  projectSetup,
  setProjectSetup,
  socialsFormData,
  setSocialsFormData,
  projectId,
  isLive,
  setIsLive,
  setLoader,
}) => {
  const fileInputRef = useRef(null);
  const { uploadFile } = useFileUpload();

  // Local state for editing
  const [editData, setEditData] = useState({
    projectName: basicInfo.projectName.value || "",
    projectTicker: basicInfo.projectTicker.value || "",
    shortDescription: basicInfo.shortDescription.value || "",
    projectImage: projectSetup.projectImage.value || null,
    projectWebsite: socialsFormData.projectWebsite || "",
    projectTwitter: socialsFormData.projectTwitter || "",
    projectTelegram: socialsFormData.projectTelegram || "",
    projectDiscord: socialsFormData.projectDiscord || "",
    projectLinkedIn: socialsFormData.projectLinkedIn || "",
    projectMedium: socialsFormData.projectMedium || "",
    projectYoutube: socialsFormData.projectYoutube || "",
  });

  /* ---------------- CROP STATES ---------------- */

  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const cropAspect = 1; // 1:1 square

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Validation errors state
  const [errors, setErrors] = useState({
    projectName: "",
    projectTicker: "",
    shortDescription: "",
    projectImage: "",
    projectWebsite: "",
    projectTwitter: "",
    projectTelegram: "",
    projectDiscord: "",
    projectLinkedIn: "",
    projectMedium: "",
    projectYoutube: "",
  });

  const [isRemoved, setIsRemoved] = useState(!projectSetup.projectImage.value);

  // Validation functions
  const validateURL = (url, platform = "website") => {
    if (!url || url.trim() === "") return true; // Optional fields

    const urlPattern =
      /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

    if (!urlPattern.test(url)) {
      return false;
    }

    // Platform-specific validation
    if (platform === "twitter") {
      return url.includes("twitter.com") || url.includes("x.com");
    }
    if (platform === "telegram") {
      return url.includes("t.me") || url.includes("telegram");
    }

    return true;
  };

  const validateForm = () => {
    const newErrors = {
      projectName: "",
      projectTicker: "",
      shortDescription: "",
      projectImage: "",
      projectWebsite: "",
      projectTwitter: "",
      projectTelegram: "",
    };

    let isValid = true;

    // Project Name - Required
    if (!editData.projectName || editData.projectName.trim() === "") {
      newErrors.projectName = "Project name is required";
      isValid = false;
    }

    // Project Ticker - Required
    if (!editData.projectTicker || editData.projectTicker.trim() === "") {
      newErrors.projectTicker = "Project ticker is required";
      isValid = false;
    }

    // Short Description - Required
    if (!editData.shortDescription || editData.shortDescription.trim() === "") {
      newErrors.shortDescription = "Short description is required";
      isValid = false;
    } else if (editData.shortDescription.trim().length < 1) {
      newErrors.shortDescription = "Description is required";
      isValid = false;
    }

    // Project Image - Required
    if (!editData.projectImage) {
      newErrors.projectImage = "Project image is required";
      isValid = false;
    }

    // Project Website - Required
    if (!editData.projectWebsite || editData.projectWebsite.trim() === "") {
      newErrors.projectWebsite = "Project website is required";
      isValid = false;
    } else if (!validateURL(editData.projectWebsite)) {
      newErrors.projectWebsite = "Please enter a valid URL";
      isValid = false;
    }

    // Twitter - Optional but validate if provided
    if (editData.projectTwitter && editData.projectTwitter.trim() !== "") {
      if (!validateURL(editData.projectTwitter, "twitter")) {
        newErrors.projectTwitter = "Please enter a valid Twitter/X URL";
        isValid = false;
      }
    }

    // Telegram - Optional but validate if provided
    if (editData.projectTelegram && editData.projectTelegram.trim() !== "") {
      if (!validateURL(editData.projectTelegram, "telegram")) {
        newErrors.projectTelegram = "Please enter a valid Telegram URL";
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle input changes
  const handleChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  // Image handling
  const handleReplace = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        projectImage: "File size must be less than 5MB",
      }));
      return;
    }

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        projectImage: "Only JPEG and PNG formats are allowed",
      }));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setSelectedFile(file);
      setShowCropModal(true); // 🔥 OPEN CROP MODAL
    };
    reader.readAsDataURL(file);
  };
  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImg = async () => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 400;
    canvas.height = 400;

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      400,
      400
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/jpeg");
    });
  };
  const handleCropSave = async () => {
    try {
      setLoader(true);

      const croppedBlob = await getCroppedImg();

      const croppedFile = new File([croppedBlob], selectedFile.name, {
        type: "image/jpeg",
      });

      const uploadedUrl = await uploadFile(croppedFile, setLoader);

      setEditData((prev) => ({
        ...prev,
        projectImage: uploadedUrl,
      }));

      setErrors((prev) => ({ ...prev, projectImage: "" }));
      setIsRemoved(false);
      setShowCropModal(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const handleRemove = () => {
    setEditData((prev) => ({
      ...prev,
      projectImage: null,
    }));
    setIsRemoved(true);
  };

  const handleCancel = () => {
    setEditData({
      projectName: basicInfo.projectName.value || "",
      projectTicker: basicInfo.projectTicker.value || "",
      shortDescription: basicInfo.shortDescription.value || "",
      projectImage: projectSetup.projectImage.value || null,
      projectWebsite: socialsFormData.projectWebsite || "",
      projectTwitter: socialsFormData.projectTwitter || "",
      projectTelegram: socialsFormData.projectTelegram || "",
      projectDiscord: socialsFormData.projectDiscord || "",
      projectLinkedIn: socialsFormData.projectLinkedIn || "",
      projectMedium: socialsFormData.projectMedium || "",
      projectYoutube: socialsFormData.projectYoutube || "",
    });
    setErrors({
      projectName: "",
      projectTicker: "",
      shortDescription: "",
      projectImage: "",
      projectWebsite: "",
      projectTwitter: "",
      projectTelegram: "",
      projectDiscord: "",
      projectLinkedIn: "",
      projectMedium: "",
      projectYoutube: "",
    });
    setIsRemoved(!projectSetup.projectImage.value);
    handleClose();
  };

  const handleSaveChanges = () => {
    // Validate form
    if (!validateForm()) {
      return;
    }

    // Update basicInfo
    setBasicInfo((prev) => ({
      ...prev,
      projectName: {
        ...prev.projectName,
        value: editData.projectName,
      },
      projectTicker: {
        ...prev.projectTicker,
        value: editData.projectTicker,
      },
      shortDescription: {
        ...prev.shortDescription,
        value: editData.shortDescription,
      },
    }));

    // Update projectSetup
    setProjectSetup((prev) => ({
      ...prev,
      projectImage: {
        ...prev.projectImage,
        value: editData.projectImage,
      },
    }));

    // Update socialsFormData with all social links
    setSocialsFormData((prev) => ({
      ...prev,
      projectWebsite: editData.projectWebsite || "",
      projectTwitter: editData.projectTwitter || "",
      projectTelegram: editData.projectTelegram || "",
      projectDiscord: editData.projectDiscord || "",
      projectLinkedIn: editData.projectLinkedIn || "",
      projectMedium: editData.projectMedium || "",
      projectYoutube: editData.projectYoutube || "",
    }));

    handleClose();
  };

  return (
    <>
      <Modal
        className="publish-modall"
        show={show}
        onHide={handleCancel}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="publish_modal">
            <div className="edit_info">
              {/* Image Section */}
              <div className="new_edit">
                <div className="edit_main">
                  {editData.projectImage ? (
                    <img src={editData.projectImage} alt="Project" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="38"
                      height="38"
                      viewBox="0 0 38 38"
                      fill="none"
                    >
                      <g clipPath="url(#clip0_478_3776)">
                        <path
                          d="M27.6332 37.9994C27.3672 37.9994 27.0948 37.9662 26.8257 37.8949L2.34258 31.3383C0.664251 30.876 -0.336416 29.1375 0.103751 27.4592L3.19283 15.9452C3.30683 15.5224 3.74067 15.277 4.16183 15.3847C4.58458 15.4971 4.83475 15.9325 4.72233 16.3537L1.63483 27.8645C1.41475 28.7037 1.91825 29.5777 2.759 29.8104L27.2326 36.3638C28.0733 36.5855 28.941 36.0852 29.1595 35.2492L30.3961 30.667C30.5101 30.2442 30.9439 29.9925 31.3667 30.1081C31.7894 30.2221 32.038 30.6575 31.9256 31.0787L30.6906 35.6545C30.3185 37.0637 29.036 37.9994 27.6332 37.9994Z"
                          fill="black"
                        />
                        <path
                          d="M34.832 28.4993H9.4987C7.75228 28.4993 6.33203 27.0791 6.33203 25.3327V6.33268C6.33203 4.58627 7.75228 3.16602 9.4987 3.16602H34.832C36.5784 3.16602 37.9987 4.58627 37.9987 6.33268V25.3327C37.9987 27.0791 36.5784 28.4993 34.832 28.4993ZM9.4987 4.74935C8.62628 4.74935 7.91536 5.46027 7.91536 6.33268V25.3327C7.91536 26.2051 8.62628 26.916 9.4987 26.916H34.832C35.7044 26.916 36.4154 26.2051 36.4154 25.3327V6.33268C36.4154 5.46027 35.7044 4.74935 34.832 4.74935H9.4987Z"
                          fill="black"
                        />
                        <path
                          d="M14.2487 14.2493C12.5023 14.2493 11.082 12.8291 11.082 11.0827C11.082 9.33627 12.5023 7.91602 14.2487 7.91602C15.9951 7.91602 17.4154 9.33627 17.4154 11.0827C17.4154 12.8291 15.9951 14.2493 14.2487 14.2493ZM14.2487 9.49935C13.3763 9.49935 12.6654 10.2103 12.6654 11.0827C12.6654 11.9551 13.3763 12.666 14.2487 12.666C15.1211 12.666 15.832 11.9551 15.832 11.0827C15.832 10.2103 15.1211 9.49935 14.2487 9.49935Z"
                          fill="black"
                        />
                        <path
                          d="M7.23542 26.8067C7.03276 26.8067 6.83009 26.7292 6.67492 26.5756C6.36617 26.2668 6.36617 25.7649 6.67492 25.4562L14.153 17.9781C15.0492 17.0819 16.6151 17.0819 17.5113 17.9781L19.7374 20.2042L25.8998 12.8101C26.3478 12.2733 27.0065 11.9614 27.7079 11.9551H27.7253C28.4188 11.9551 29.0759 12.2559 29.5288 12.7832L37.8096 22.4447C38.0946 22.7756 38.0566 23.2759 37.7241 23.5609C37.3932 23.8459 36.8944 23.8095 36.6078 23.4754L28.327 13.8139C28.1734 13.6366 27.9613 13.5384 27.7253 13.5384C27.5607 13.5242 27.2709 13.6382 27.1173 13.8234L20.3993 21.8842C20.2568 22.0552 20.0493 22.1581 19.8261 22.1676C19.6013 22.1834 19.3875 22.0947 19.2308 21.9364L16.3918 19.0975C16.0926 18.7998 15.5717 18.7998 15.2724 19.0975L7.79434 26.5756C7.64075 26.7292 7.43809 26.8067 7.23542 26.8067Z"
                          fill="black"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_478_3776">
                          <rect width="38" height="38" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  )}
                </div>

                <div>
                  <h2>Project Image</h2>
                  <p>
                    We recommend to upload images in 400x400 resolution. Max 5
                    MB in JPEG or PNG format
                  </p>
                  <div className="buttons_bothhhh">
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                    />
                    <div className="buttonbothsection">
                      <button className="replacce" onClick={handleReplace}>
                        {isRemoved ? "Upload Image" : "Replace"}
                      </button>

                      <button className="removetext">Remove</button>
                    </div>
                  </div>
                  {errors.projectImage && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "8px",
                      }}
                    >
                      {errors.projectImage}
                    </p>
                  )}
                </div>
              </div>

              {/* Project Name & Ticker */}
              <div className="name_main">
                <div>
                  <h3>Project Name</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectName}
                      onChange={(e) =>
                        handleChange("projectName", e.target.value)
                      }
                      placeholder="Enter project name"
                      className={errors.projectName ? "error" : ""}
                    />
                  </div>
                  {errors.projectName && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectName}
                    </p>
                  )}
                </div>
                <div>
                  <h3>Project Ticker</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectTicker}
                      onChange={(e) =>
                        handleChange("projectTicker", e.target.value)
                      }
                      placeholder="Enter ticker"
                      className={errors.projectTicker ? "error" : ""}
                    />
                  </div>
                  {errors.projectTicker && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectTicker}
                    </p>
                  )}
                </div>
              </div>

              {/* Project Short Description */}
              <div>
                <h3>Project Short Description</h3>
                <div className="inner_edddit">
                  <textarea
                    value={editData.shortDescription}
                    onChange={(e) =>
                      handleChange("shortDescription", e.target.value)
                    }
                    placeholder="Enter short description"
                    rows={3}
                    className={errors.shortDescription ? "error" : ""}
                  />
                </div>
                {errors.shortDescription && (
                  <p
                    className="error-text"
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {errors.shortDescription}
                  </p>
                )}
              </div>

              {/* Socials */}
              <h5>SOCIALS</h5>
              <div className="projectsss">
                <div>
                  <h3>
                    Project Website <span style={{ color: "red" }}>*</span>
                  </h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectWebsite}
                      onChange={(e) =>
                        handleChange("projectWebsite", e.target.value)
                      }
                      placeholder="Enter Project website"
                      className={errors.projectWebsite ? "error" : ""}
                    />
                  </div>
                  {errors.projectWebsite && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectWebsite}
                    </p>
                  )}
                </div>
                <div>
                  <h3>
                    Twitter{" "}
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      (Optional)
                    </span>
                  </h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectTwitter}
                      onChange={(e) =>
                        handleChange("projectTwitter", e.target.value)
                      }
                      placeholder="Enter twitter handle"
                      className={errors.projectTwitter ? "error" : ""}
                    />
                  </div>
                  {errors.projectTwitter && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectTwitter}
                    </p>
                  )}
                </div>
                <div>
                  <h3>
                    Telegram{" "}
                    <span style={{ color: "#999", fontSize: "12px" }}>
                      (Optional)
                    </span>
                  </h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectTelegram}
                      onChange={(e) =>
                        handleChange("projectTelegram", e.target.value)
                      }
                      placeholder="Enter telegram link"
                      className={errors.projectTelegram ? "error" : ""}
                    />
                  </div>
                  {errors.projectTelegram && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectTelegram}
                    </p>
                  )}
                </div>
                <div>
                  <h3>Medium</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectMedium}
                      onChange={(e) =>
                        handleChange("projectMedium", e.target.value)
                      }
                      placeholder="Enter medium link"
                      className={errors.projectMedium ? "error" : ""}
                    />
                  </div>
                  {errors.projectMedium && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectMedium}
                    </p>
                  )}
                </div>
                <div>
                  <h3>Youtube</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectYoutube}
                      onChange={(e) =>
                        handleChange("projectYoutube", e.target.value)
                      }
                      placeholder="Enter youtube link"
                      className={errors.projectYoutube ? "error" : ""}
                    />
                  </div>
                  {errors.projectYoutube && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectYoutube}
                    </p>
                  )}
                </div>

                <div>
                  <h3>Discord</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectDiscord}
                      onChange={(e) =>
                        handleChange("projectDiscord", e.target.value)
                      }
                      placeholder="Enter discord link"
                      className={errors.projectDiscord ? "error" : ""}
                    />
                  </div>
                  {errors.projectDiscord && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectDiscord}
                    </p>
                  )}
                </div>

                <div>
                  <h3>Linkedin</h3>
                  <div className="inner_edddit">
                    <input
                      type="text"
                      value={editData.projectLinkedIn}
                      onChange={(e) =>
                        handleChange("projectLinkedIn", e.target.value)
                      }
                      placeholder="Enter linkedin link"
                      className={errors.projectLinkedIn ? "error" : ""}
                    />
                  </div>
                  {errors.projectLinkedIn && (
                    <p
                      className="error-text"
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {errors.projectLinkedIn}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div className="publish_buttons">
              <button onClick={handleCancel} className="cancel_button">
                Cancel
              </button>
              <button className="confirm_button" onClick={handleSaveChanges}>
                Save Changes
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        className="publish-modall"
        show={showCropModal}
        onHide={() => setShowCropModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Image</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="publish_modal">
            <div className="croopppy">
              <div className="cropmodal">
                {imageSrc && (
                  <div
                    className="crop-container inercrop"
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "240px",
                      background: "#E7EEF2",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={cropAspect}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                )}
              </div>

              <p>Drag to reposition image</p>

              <div className="zoom-slider">
                <h5>Zoom</h5>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{
                    width: "100%",
                    height: "8px",
                    borderRadius: "10px",
                    appearance: "none",
                  }}
                />
              </div>

              <div className="publish_buttons">
                <button
                  className="cancel_button"
                  onClick={() => setShowCropModal(false)}
                >
                  Cancel
                </button>
                <button className="confirm_button" onClick={handleCropSave}>
                  Save Cropped Image
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EditInfoModal;
